import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { filterVehiclesByTerm } from 'hooks/inventory/useVehicleSearch'
import { filterLeadsByTerm } from 'hooks/admin/useLeadSearch'
import { filterInvoicesByTerm } from 'hooks/admin/useInvoiceSearch'
import { filterTransporterPackagesByTerm } from 'hooks/admin/useTransporterPackageSearch'
import { buildClientSearchResults } from 'hooks/admin/useClientUserSearch'
import type { EntityScope } from './parseSlashQuery'
import {
  fetchClientUsers,
  fetchInvoices,
  fetchLeads,
  fetchMasterInventory,
  fetchShipmentPlans,
  fetchTransporterPackages,
  fetchTransportTrucks,
  prefetchEntityCaches,
  SEARCH_CACHE_STALE_MS,
  SEARCH_QUERY_KEYS,
} from './searchCaches'

interface UseEntitySearchOptions {
  open: boolean
  enabled: boolean
  scope: EntityScope | null
  query: string
}

export function useEntitySearch({ open, enabled, scope, query }: UseEntitySearchOptions) {
  const queryClient = useQueryClient()
  const [warmed, setWarmed] = useState(false)

  useEffect(() => {
    if (!open || !enabled) return
    let cancelled = false
    void prefetchEntityCaches(queryClient).finally(() => {
      if (!cancelled) setWarmed(true)
    })
    return () => {
      cancelled = true
    }
  }, [open, enabled, queryClient])

  const subscribe = open && enabled

  const { data: vehicles = [] } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.inventory,
    queryFn: fetchMasterInventory,
    enabled: subscribe,
    staleTime: SEARCH_CACHE_STALE_MS,
  })
  const { data: leads = [] } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.leads,
    queryFn: fetchLeads,
    enabled: subscribe,
    staleTime: SEARCH_CACHE_STALE_MS,
  })
  const { data: invoices = [] } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.invoices,
    queryFn: fetchInvoices,
    enabled: subscribe,
    staleTime: SEARCH_CACHE_STALE_MS,
  })
  const { data: clientUsers = [] } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.clientUsers,
    queryFn: fetchClientUsers,
    enabled: subscribe,
    staleTime: SEARCH_CACHE_STALE_MS,
  })
  const { data: packages = [] } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.packages,
    queryFn: fetchTransporterPackages,
    enabled: subscribe,
    staleTime: SEARCH_CACHE_STALE_MS,
  })
  const { data: plans = [] } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.shipmentPlans,
    queryFn: fetchShipmentPlans,
    enabled: subscribe,
    staleTime: SEARCH_CACHE_STALE_MS,
  })
  const { data: trucks = [] } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.transportTrucks,
    queryFn: fetchTransportTrucks,
    enabled: subscribe,
    staleTime: SEARCH_CACHE_STALE_MS,
  })

  const wantsEntities =
    enabled &&
    query.trim().length >= 2 &&
    (scope === null ||
      scope === 'inventory' ||
      scope === 'lead' ||
      scope === 'invoice' ||
      scope === 'client' ||
      scope === 'transport')

  const inventory = useMemo(
    () =>
      !wantsEntities || (scope && scope !== 'inventory' && scope !== null)
        ? []
        : scope === 'inventory' || scope === null
          ? filterVehiclesByTerm(vehicles, query)
          : [],
    [wantsEntities, scope, vehicles, query],
  )

  const leadResults = useMemo(
    () =>
      !wantsEntities || (scope && scope !== 'lead')
        ? []
        : filterLeadsByTerm(leads, vehicles, query),
    [wantsEntities, scope, leads, vehicles, query],
  )

  const invoiceResults = useMemo(
    () =>
      !wantsEntities || (scope && scope !== 'invoice')
        ? []
        : filterInvoicesByTerm(invoices, query),
    [wantsEntities, scope, invoices, query],
  )

  const transportResults = useMemo(
    () =>
      !wantsEntities || (scope && scope !== 'transport')
        ? []
        : filterTransporterPackagesByTerm(packages, vehicles, query),
    [wantsEntities, scope, packages, vehicles, query],
  )

  const clientResults = useMemo(
    () =>
      !wantsEntities || (scope && scope !== 'client')
        ? []
        : buildClientSearchResults(clientUsers, plans, leads, trucks, vehicles, query),
    [wantsEntities, scope, clientUsers, plans, leads, trucks, vehicles, query],
  )

  return {
    inventory,
    leadResults,
    invoiceResults,
    transportResults,
    clientResults,
    isLoading: wantsEntities && !warmed,
  }
}
